import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { 
  EyeIcon, 
  EyeSlashIcon, 
  LockClosedIcon,
  EnvelopeIcon,
  VideoCameraIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { signInUser, getUserData } from '../services/firebaseService';

const loginSchema = yup.object({
  email: yup.string().required('Email is required').email('Invalid email format'),
  password: yup.string().required('Password is required').min(6, 'Password must be at least 6 characters')
});

interface LoginFormData {
  email: string;
  password: string;
}

const InstructorLoginPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const navigate = useNavigate();

  const { 
    register, 
    handleSubmit, 
    formState: { errors } 
  } = useForm<LoginFormData>({
    resolver: yupResolver(loginSchema)
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setError('');

    try {
      const firebaseUser = await signInUser(data.email, data.password);
      const userData = await getUserData(firebaseUser.uid);

      if (!userData) {
        throw new Error('User profile record not found. Please contact administration.');
      }

      const allowedRoles = ['instructor', 'admin', 'super_admin'];
      if (!allowedRoles.includes(userData.role)) {
        throw new Error('Access Denied: This portal is reserved for Instructors. Your account does not have instructor privileges.');
      }

      // Save instructor session
      localStorage.setItem('instructorUser', JSON.stringify(userData));

      // Redirect to instructor dashboard
      navigate('/instructor/dashboard');
    } catch (err: any) {
      console.error('Instructor login error:', err);
      setError(err.message || 'Invalid email or password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 relative z-10">
        {/* Header */}
        <div className="text-center">
          <Link to="/" className="inline-flex items-center space-x-3 mb-6">
            <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/30">
              <VideoCameraIcon className="w-7 h-7 text-white" />
            </div>
            <div className="text-left">
              <div className="text-white font-bold text-xl tracking-tight">Kreed Odisha</div>
              <div className="text-indigo-300 text-xs font-medium uppercase tracking-wider">Instructor Portal</div>
            </div>
          </Link>

          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2 tracking-tight">
            Instructor Sign In
          </h2>
          <p className="text-slate-300 text-sm">
            Sign in to manage your stream and conduct live training sessions
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/10 shadow-2xl">
          {error && (
            <div className="mb-6 bg-red-500/15 border border-red-500/40 rounded-xl p-4 text-red-200 text-sm flex items-start space-x-3">
              <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0 text-red-400 mt-0.5" />
              <div className="flex-1 font-medium">{error}</div>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-200 mb-1.5">
                Instructor Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <EnvelopeIcon className="h-5 w-5 text-slate-400" />
                </div>
                <input
                  {...register('email')}
                  type="email"
                  autoComplete="email"
                  placeholder="instructor@okreed.in"
                  className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/15 rounded-xl text-white placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-xs text-red-400">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-200 mb-1.5">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <LockClosedIcon className="h-5 w-5 text-slate-400" />
                </div>
                <input
                  {...register('password')}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  className="w-full pl-10 pr-10 py-2.5 bg-white/5 border border-white/15 rounded-xl text-white placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-white transition-colors"
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-xs text-red-400">{errors.password.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 px-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white rounded-xl font-semibold text-sm shadow-lg shadow-indigo-500/25 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Signing In...</span>
                </>
              ) : (
                <span>Sign In to Instructor Portal</span>
              )}
            </button>
          </form>

          {/* Quick Notice */}
          <div className="mt-6 pt-6 border-t border-white/10 text-center">
            <p className="text-xs text-slate-400">
              Instructor accounts are provisioned by the system administrator. If you do not have an account, please contact administrative support.
            </p>
          </div>
        </div>

        {/* Footer Navigation Links */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-400 px-2">
          <Link to="/admin/login" className="hover:text-white transition-colors flex items-center space-x-1">
            <span>&larr; Admin Portal</span>
          </Link>
          <Link to="/class" className="hover:text-white transition-colors flex items-center space-x-1">
            <span>Student Classroom Portal &rarr;</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default InstructorLoginPage;
